import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import ForkliftIcon from "@mui/icons-material/Agriculture";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { selectUser } from "../../features/userSlice";
import { Card, PageHeader, PageShell, SectionHeader } from "../../components/UI/Page";
import { EmptyState, LoadingState, StatTile } from "../../components/UI/States";
import { useLocations } from "../../components/Fleet/Forklifts/Utilities/useLocations";
import {
  ActivityCalendar,
  BackLink,
  Fact,
  InspectionDialog,
  InspectionTable,
  Notice,
  Pill,
  Segmented,
  ServiceChip,
  ServiceDialog,
  ServiceNotice,
} from "../../components/Fleet/FleetParts";
import {
  HISTORY_DAYS,
  checkinTone,
  downloadText,
  formatDate,
  formatDateTime,
  formatDuration,
  inspectionCsvColumns,
  isFlagged,
  localDayKey,
  relativeDay,
  toCsv,
} from "../../components/Fleet/fleetFormat";
import { ConfirmDialog, EditForkliftDialog } from "../../components/Fleet/ForkliftDialogs";
import { deleteForklift, fetchForklift, setForkliftService } from "./fleetApi";
import { lastCheckIn } from "./ForkliftList";

const Contact = ({ title, name, email, phone }) => (
  <div className="app-surface min-w-0 rounded-xl border p-3">
    <p className="app-eyebrow">{title}</p>
    <p className="mt-0.5 truncate font-semibold">{name || "—"}</p>
    {email && (
      <a href={`mailto:${email}`} className="app-link mt-0.5 flex items-center gap-1.5 truncate text-sm">
        <EmailRoundedIcon sx={{ fontSize: 15 }} />
        <span className="truncate">{email}</span>
      </a>
    )}
    {phone && (
      <a href={`tel:${phone}`} className="app-link mt-0.5 flex items-center gap-1.5 text-sm tabular-nums">
        <PhoneRoundedIcon sx={{ fontSize: 15 }} />
        {phone}
      </a>
    )}
  </div>
);

// Opened from a location's page in Location Manager, the back link returns
// there; otherwise it goes to the Forklifts list.
const backFrom = (state) => {
  const backTo = state?.backTo;
  return typeof backTo?.to === "string" && backTo.to.startsWith("/") && backTo.label
    ? { to: backTo.to, label: String(backTo.label) }
    : { to: "/fleet/forklifts", label: "Forklifts" };
};

const ForkliftDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const back = backFrom(useLocation().state);
  const user = useSelector(selectUser);
  const { locations } = useLocations();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");
  const [shiftFilter, setShiftFilter] = useState("all");
  const [day, setDay] = useState(null);
  const [dialog, setDialog] = useState(null); // "edit" | "service" | "delete"
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setError(null);
    try {
      setData(await fetchForklift(user.id, id));
    } catch (e) {
      setError(e.message);
    }
  }, [user?.id, id]);

  useEffect(() => {
    setData(null);
    setDay(null);
    load();
  }, [load]);

  const entries = useMemo(() => data?.entries || [], [data]);
  const shown = useMemo(
    () =>
      entries
        .filter((e) =>
          filter === "all" ? true : filter === "service" ? e.source === "status" : isFlagged(e)
        )
        .filter((e) => shiftFilter === "all" || e.shift === shiftFilter)
        .filter((e) => !day || localDayKey(e.at) === day),
    [entries, filter, shiftFilter, day]
  );

  const locationNames = useMemo(() => locations.map((l) => l.location).filter(Boolean), [locations]);

  const closeDialog = () => {
    setDialog(null);
    setActionError(null);
  };

  const run = async (action) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      closeDialog();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) {
    return (
      <PageShell size="2xl">
        <BackLink to={back.to}>{back.label}</BackLink>
        <div className="app-card rounded-2xl border">
          <EmptyState icon={ForkliftIcon} title="Couldn't open this forklift." description={error} />
        </div>
      </PageShell>
    );
  }

  if (!data) {
    return (
      <PageShell size="2xl">
        <BackLink to={back.to}>{back.label}</BackLink>
        <LoadingState label="Loading forklift…" />
      </PageShell>
    );
  }

  const { forklift, contacts, locationId } = data;
  const last = lastCheckIn(forklift);
  const flaggedCount = entries.filter(isFlagged).length;
  // History also carries out-of-service changes; counts are check-ins only.
  const checkIns = entries.filter((e) => e.source !== "status");
  const shifts = data.shifts || [];
  const exportCsv = () =>
    downloadText(
      `forklift-${forklift.unit}-checkins.csv`,
      toCsv(shown, inspectionCsvColumns(shown))
    );
  const checklistPath = forklift.location
    ? `/fleet/forks/forkliftform/${encodeURIComponent(forklift.location)}/${encodeURIComponent(forklift.unit)}?m=${forklift.moffett}`
    : null;
  const checklistUrl = checklistPath ? `${window.location.origin}${checklistPath}` : "";

  return (
    <PageShell size="2xl">
      <InspectionDialog entry={open} onClose={() => setOpen(null)} userId={user?.id} />
      <EditForkliftDialog
        forklift={forklift}
        open={dialog === "edit"}
        onClose={closeDialog}
        onSaved={() => {
          closeDialog();
          load();
        }}
        locations={locationNames}
        userId={user?.id}
      />
      {/* Out of service blocks check-ins on the branch kiosk until it's
          put back; the reason is logged in the unit's history. */}
      <ServiceDialog
        open={dialog === "service"}
        assetName={`unit #${forklift.unit}`}
        inService={forklift.inService}
        onClose={closeDialog}
        onSubmit={async (change) => {
          await setForkliftService(user.id, forklift.id, change);
          closeDialog();
          await load();
        }}
      />
      <ConfirmDialog
        open={dialog === "delete"}
        title={`Delete unit #${forklift.unit}?`}
        body="It's removed from the fleet, the branch kiosk and the mobile app. Its check-in history is kept and still shows in the Inspection Log."
        confirmLabel="Delete forklift"
        danger
        busy={busy}
        error={actionError}
        onClose={closeDialog}
        onConfirm={() =>
          run(async () => {
            await deleteForklift(user.id, forklift.id);
            navigate(back.to, { replace: true });
          })
        }
      />

      <BackLink to={back.to}>{back.label}</BackLink>
      <PageHeader
        icon={ForkliftIcon}
        tone="teal"
        eyebrow="Forklift"
        title={`Unit #${forklift.unit}`}
        description={forklift.location || "No branch assigned"}
        actions={
          <>
            <button type="button" className="app-btn app-btn-secondary" onClick={() => setDialog("edit")}>
              <EditRoundedIcon sx={{ fontSize: 18 }} /> Edit
            </button>
            <button
              type="button"
              className={`app-btn ${forklift.inService ? "app-btn-secondary" : "app-btn-primary"}`}
              onClick={() => setDialog("service")}
            >
              <PowerSettingsNewRoundedIcon sx={{ fontSize: 18 }} />
              {forklift.inService ? "Mark out of service" : "Mark in service"}
            </button>
            <button
              type="button"
              className="app-icon-btn text-rose-600 dark:text-rose-400"
              onClick={() => setDialog("delete")}
              aria-label="Delete forklift"
              title="Delete forklift"
            >
              <DeleteOutlineRoundedIcon />
            </button>
          </>
        }
      >
        <div className="mt-3 flex flex-wrap gap-2">
          <ServiceChip inService={forklift.inService} service={forklift.service} />
          {forklift.moffett && <Pill tone="violet">Moffett</Pill>}
        </div>
      </PageHeader>

      {error && <Notice tone="rose">{error}</Notice>}
      <ServiceNotice service={forklift.service} />

      <div className={`mb-6 grid grid-cols-2 gap-3 ${forklift.stats.timedCount ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        <StatTile label="Last check-in" value={relativeDay(last)} hint={last ? formatDateTime(last) : "No check-ins on record"} tone={{ emerald: "emerald", amber: "amber", rose: "rose" }[checkinTone(last)]} />
        <StatTile label="Check-ins" value={checkIns.length} hint={`Last ${HISTORY_DAYS} days`} />
        {/* Mobile check-ins carry a timer once the app's checklist overhaul ships. */}
        {forklift.stats.timedCount > 0 && (
          <StatTile
            label="Typical check-in time"
            value={formatDuration(forklift.stats.medianSeconds)}
            hint={`${forklift.stats.quickCount} of ${forklift.stats.timedCount} under 30 seconds`}
            tone={forklift.stats.quickCount ? "amber" : "default"}
          />
        )}
        <StatTile
          label="Flagged"
          value={flaggedCount}
          hint="Needs attention or not safe"
          tone={flaggedCount ? "amber" : "default"}
          active={filter === "flagged"}
          onClick={flaggedCount ? () => setFilter(filter === "flagged" ? "all" : "flagged") : undefined}
        />
        <StatTile label="Last inspector" value={forklift.stats.lastInspector || "—"} hint={forklift.stats.lastSource === "kiosk" ? "Branch kiosk" : forklift.stats.lastSource === "mobile" ? "Mobile app" : undefined} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Card>
            <SectionHeader title="Activity" description="Click a day to see just that day's check-ins." />
            <ActivityCalendar entries={entries} selectedDay={day} onSelectDay={setDay} noun="check-in" />
          </Card>

          <div className="app-card overflow-hidden rounded-2xl border">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-5">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Check-in history</h2>
                <p className="app-muted text-sm">Mobile app and branch kiosk, last {HISTORY_DAYS} days.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {day && (
                  <button type="button" onClick={() => setDay(null)} className="app-chip">
                    {formatDate(`${day}T12:00:00`)} <CloseRoundedIcon sx={{ fontSize: 14 }} />
                  </button>
                )}
                {shifts.length > 0 && (
                  <Segmented
                    label="Filter by shift"
                    value={shiftFilter}
                    onChange={setShiftFilter}
                    options={[
                      { value: "all", label: "All shifts" },
                      ...shifts.map((s) => ({
                        value: s.id,
                        label: s.name,
                        count: entries.filter((e) => e.shift === s.id).length,
                      })),
                    ]}
                  />
                )}
                <Segmented
                  label="Filter check-ins"
                  value={filter}
                  onChange={setFilter}
                  options={[
                    { value: "all", label: "All", count: entries.length },
                    { value: "flagged", label: "Flagged", count: flaggedCount },
                    { value: "service", label: "Service changes", count: entries.length - checkIns.length },
                  ]}
                />
                <button
                  type="button"
                  className="app-btn app-btn-sm app-btn-secondary"
                  onClick={exportCsv}
                  disabled={!shown.length}
                  title="Download the check-ins shown, with every checklist answer"
                >
                  <DownloadRoundedIcon sx={{ fontSize: 16 }} /> Export CSV
                </button>
              </div>
            </div>
            <div className="mt-3 border-t app-divider">
              <InspectionTable
                entries={shown}
                onOpen={setOpen}
                show={{ source: true }}
                emptyTitle={entries.length ? "No check-ins match this filter." : `No check-ins in the last ${HISTORY_DAYS} days.`}
              />
            </div>
          </div>
        </div>

        <div className="min-w-0 space-y-6">
          {shifts.length > 0 && (
            <Card>
              <SectionHeader title="By shift" description={`${forklift.location} runs ${shifts.length} shifts on this unit.`} />
              <ul className="space-y-2">
                {shifts.map((s) => {
                  const mine = entries.filter((e) => e.shift === s.id);
                  return (
                    <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
                      <span className="min-w-0">
                        <span className="block font-semibold">{s.name}</span>
                        <span className="app-muted block text-xs tabular-nums">
                          {mine[0] ? `Last ${formatDateTime(mine[0].at)} · ${mine[0].inspector}` : "No check-ins"}
                        </span>
                      </span>
                      <span className="shrink-0 tabular-nums">{mine.length}</span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}

          <Card>
            <SectionHeader title="Details" />
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Fact label="Branch">{forklift.location}</Fact>
              <Fact label="Type">{forklift.moffett ? "Moffett" : "Forklift"}</Fact>
              <Fact label="Added">{forklift.dateAdded ? formatDate(forklift.dateAdded) : ""}</Fact>
              <Fact label="Added by">{forklift.addedBy}</Fact>
              {forklift.oldUnit && forklift.oldUnit !== forklift.unit && <Fact label="Previous unit #">{forklift.oldUnit}</Fact>}
              {forklift.oldLocation && forklift.oldLocation !== forklift.location && (
                <Fact label="Previous branch">{forklift.oldLocation}</Fact>
              )}
            </dl>
          </Card>

          {checklistPath && (
            <Card>
              <SectionHeader title="Branch kiosk" description="The checklist link is what the code on the machine opens." />
              <div className="flex flex-col gap-2">
                <Link to={`/fleet/forks/dashboard/${encodeURIComponent(forklift.location)}`} target="_blank" className="app-btn app-btn-secondary justify-between">
                  {forklift.location} kiosk <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
                </Link>
                <div className="app-surface flex items-center gap-2 rounded-xl border px-3 py-2">
                  <span className="min-w-0 flex-1 truncate font-mono text-xs" title={checklistUrl}>
                    {checklistUrl}
                  </span>
                  <button
                    type="button"
                    className="app-icon-btn shrink-0"
                    aria-label="Copy checklist link"
                    title="Copy checklist link"
                    onClick={() => {
                      navigator.clipboard?.writeText(checklistUrl).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      });
                    }}
                  >
                    <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
                {copied && <p className="text-xs text-emerald-700 dark:text-emerald-300">Link copied.</p>}
              </div>
            </Card>
          )}

          <Card>
            <SectionHeader
              title="Branch contacts"
              description={contacts ? "From Location Manager." : undefined}
              actions={
                locationId && (
                  <Link to={`/fleet/forks/locationmanager/${locationId}`} className="app-link text-sm font-medium">
                    Edit
                  </Link>
                )
              }
            />
            {contacts ? (
              <div className="space-y-3">
                <Contact title="Operations manager" name={contacts.managerName} email={contacts.managerEmail} phone={contacts.managerPhone} />
                <Contact title="General manager" name={contacts.generalName} email={contacts.generalEmail} phone={contacts.generalPhone} />
              </div>
            ) : (
              <p className="app-muted text-sm">
                No contacts on file for this branch.{" "}
                <Link to="/fleet/forks/locationmanager" className="app-link">
                  Add them in Location Manager
                </Link>
                .
              </p>
            )}
          </Card>
        </div>
      </div>
    </PageShell>
  );
};

export default ForkliftDetail;
